"use client";
import { useEffect, useState } from "react";
import CTALink from "@/components/CTA/ctaLink";
import { PageNames } from "@/constants/tagular/main";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import MonarchResolver from "@/monarch/MonarchResolver";
import type { RegisterCTATestResult } from "@/monarch/rules/registerCTA/types";
import { getPageType } from "@/utils/tagular/helpers";
import type { CTAProps } from "../types";
import { PageLocation } from "../types";
import CTABanner from "./Banner";
import { useAuthBaseURL } from "@/hooks/useAuthBaseURL";
import { useAuth } from "@/components/AuthProvider";
import useTranslations from "@/i18n/useTranslations";

export const registerCTADefaultData = {
  title: "Maximize Your Potential",
  subtitle:
    "Sign up for special offers, career resources, and recommendations that will help you grow, prepare, and advance in your career.",
  ctaText: "Register for free",
  location: "register-free",
  html_id: "register-free-CTA",
};

type BaseRegisterProps = CTAProps & {
  title: string;
  subtitle: string;
  ctaText: string;
  location: string;
  html_id: string;
};

function BaseRegister({
  title,
  subtitle,
  ctaText,
  theme = "light",
  location,
  html_id,
}: BaseRegisterProps) {
  const authBaseUrl = useAuthBaseURL();
  const t = useTranslations("Common");
  const tNav = useTranslations("Navigation");
  const displayTitle =
    title === registerCTADefaultData.title ? t("maximizeYourPotential") : title;
  const displaySubtitle =
    subtitle === registerCTADefaultData.subtitle
      ? t("registerCtaSubtitle")
      : subtitle;
  const displayCtaText =
    ctaText === registerCTADefaultData.ctaText ? tNav("register") : ctaText;

  return (
    <CTABanner
      title={displayTitle}
      subtitle={displaySubtitle}
      theme={theme}
      fullsize={false}
    >
      <CTALink
        label={displayCtaText}
        url={new URL("register", authBaseUrl).toString()}
        type="brand"
        location={location}
        html_id={html_id}
      />
    </CTABanner>
  );
}

// Helper function to determine if component should be hidden
function shouldHideComponent(
  data: RegisterCTATestResult["data"],
  pageLocation: string,
): boolean {
  return (
    (data?.showRegisterAtBottom && pageLocation === PageLocation.DEFAULT) ||
    (!data?.showRegisterAtBottom && pageLocation === PageLocation.BOTTOM)
  );
}

// Helper function to render BaseRegister with optional padding
function renderBaseRegister(
  data: RegisterCTATestResult["data"],
  theme: CTAProps["theme"],
  pageLocation: string,
  useCustomData = false,
) {
  const shouldShowWithPadding =
    data?.showRegisterAtBottom && pageLocation === PageLocation.BOTTOM;

  const baseRegisterProps = useCustomData
    ? {
        ...registerCTADefaultData,
        title: data?.title ?? registerCTADefaultData.title,
        subtitle: data?.subtitle ?? registerCTADefaultData.subtitle,
        ctaText: data?.ctaText ?? registerCTADefaultData.ctaText,
        theme,
      }
    : {
        ...registerCTADefaultData,
        theme,
      };

  const baseRegister = <BaseRegister {...baseRegisterProps} />;

  return shouldShowWithPadding ? (
    <div className="pt-16">{baseRegister}</div>
  ) : (
    baseRegister
  );
}

function renderMonarchDataConsumer(
  testResult: RegisterCTATestResult,
  theme: CTAProps["theme"],
  pageLocation: string,
  useCustomData = false,
) {
  const { data } = testResult;
  if (shouldHideComponent(data, pageLocation)) {
    return null;
  }
  return renderBaseRegister(data, theme, pageLocation, useCustomData);
}

export default function Register({
  theme,
  pageLocation = PageLocation.DEFAULT,
}: CTAProps) {
  const { isLoggedIn: userLoggedIn } = useAuth();
  const [pageType, setPageType] = useState("unknown");

  useEffect(() => {
    const page = getPageType();
    if (page) {
      setPageType(page);
    }
  }, []);

  if (pageType === PageNames.ShopPage) {
    return (
      <BaseRegister
        {...registerCTADefaultData}
        theme={theme}
        location="bottom-registration-banner"
        html_id="registration-CTA"
      />
    );
  }

  if (userLoggedIn) {
    return null;
  }

  return (
    <MonarchResolver<RegisterCTATestResult>
      experimentKey="useRegisterCTA"
      variants={{
        control: (
          <MonarchDataConsumer<RegisterCTATestResult>
            experimentKey="useRegisterCTA"
            render={(testResult) => {
              return renderMonarchDataConsumer(testResult, theme, pageLocation);
            }}
          />
        ),
        test: (
          <MonarchDataConsumer<RegisterCTATestResult>
            experimentKey="useRegisterCTA"
            render={(testResult) => {
              return renderMonarchDataConsumer(
                testResult,
                theme,
                pageLocation,
                true,
              );
            }}
          />
        ),
      }}
      fallbackVariant="control"
    />
  );
}
